import { FiberManualRecord } from '@mui/icons-material';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ChatIcon from '@mui/icons-material/Chat';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import InputAdornment from '@mui/material/InputAdornment';
import type { Theme } from '@mui/material/styles';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { SystemStyleObject } from '@mui/system';
import { format, isAfter } from 'date-fns';
import type { FC, MouseEvent } from 'react';
import React, { useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { Controller, useFormContext } from 'react-hook-form';

import MuiFormAutocomplete from 'components/Form/MuiFormAutocomplete/MuiFormAutocomplete';
import MuiFormInput from 'components/Form/MuiFormInput/MuiFormInput';
import MuiFormSelect from 'components/Form/MuiFormSelect/MuiFormSelect';
import type { MuiFormSelectOptionT } from 'components/Form/MuiFormSelect/MuiFormSelect';
import { MessageSourcesE } from 'declarations/enums/messages';
import { StatusIdE, TicketAssigneeE, TicketUserTypesE } from 'declarations/enums/tickets';
import { ScheduleWorkTypeE } from 'declarations/enums/tickets';
import type { TicketI } from 'declarations/interfaces/tickets';
import type { Column } from 'declarations/types/common';
import type { SelectOptionT } from 'declarations/types/select';
import { formatAmount } from 'helpers/locale';
import { getInitials } from 'helpers/string';
import ApproveIcon from 'icons/ApproveIcon';
import {
  getLastMessageDisplayText,
  isUpdateCausedByLastMessage,
  mapAgeToColor,
} from 'pages/TicketsPage/helpers';
import type { ChangeTicketsListFormValues } from 'pages/TicketsPage/hooks/useTicketsPage';

import { CHECKBOX_COLUMN_WIDTH_PX } from '../TicketsTableConfiguration';

const CHECKBOX_COLUMN_WIDTH = CHECKBOX_COLUMN_WIDTH_PX;
const CHECKBOX_SIZE = 42;
const CHECKBOX_ICON_FONT_SIZE = 24;

function getChirpChatIconSx(
  isMobile: boolean,
  fromDealer: boolean,
  isClosed: boolean
): (theme: Theme) => SystemStyleObject<Theme> {
  return (theme: Theme): SystemStyleObject<Theme> => {
    if (isClosed) {
      return {
        fontSize: isMobile ? 20 : 24,
        color: theme.palette.error.main,
      };
    }
    let color: string;
    if (theme.palette.mode === 'light') {
      color = fromDealer ? theme.palette.info.main : theme.palette.text.secondary;
    } else if (fromDealer) {
      color = theme.palette.info.main;
    } else {
      color = 'inherit';
    }
    return {
      fontSize: isMobile ? 20 : 24,
      color,
    };
  };
}

/** Renders text with truncation (ellipsis) and full value in tooltip on hover */
function TruncatedWithTooltip({
  value,
  color = 'text.primary',
}: {
  value: string | null | undefined;
  color?: 'text.primary' | 'secondary';
}): JSX.Element {
  const display = value != null && value !== '' ? value : '-';
  return (
    <Tooltip title={display} enterDelay={400} arrow placement="top-start">
      <Typography
        variant="body2"
        color={color}
        component="span"
        sx={{
          display: 'block',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {display}
      </Typography>
    </Tooltip>
  );
}

/** Replace hyphen/underscore with non-breaking variants so lines fill before wrapping */
function preventEarlyWrap(text: string): string {
  if (!text) return '';
  return text
    .replace(/-/g, '\u2011') // non-breaking hyphen
    .replace(/_/g, '_\u2060'); // word joiner after underscore prevents break before _edited
}

function formatClosedRoDateForList(roClosedDate: string | null | undefined): string {
  if (roClosedDate == null || roClosedDate === '') {
    return '';
  }
  if (!isAfter(new Date(roClosedDate), new Date('1980-01-01'))) {
    return '';
  }
  return format(new Date(roClosedDate), 'MMM do, yyyy');
}

function formatAuditTrailDisplayDate(ticket: TicketI): string {
  if (ticket.detailUpdatedDateTime) {
    const parsedDetail = Date.parse(ticket.detailUpdatedDateTime);
    if (!Number.isNaN(parsedDetail)) {
      return format(new Date(ticket.detailUpdatedDateTime), 'dd MMM yyyy h:mm a');
    }
  }
  if (ticket.updatedDateTime) {
    const parsedUpdated = Date.parse(ticket.updatedDateTime);
    if (!Number.isNaN(parsedUpdated)) {
      return format(new Date(ticket.updatedDateTime), 'dd MMM yyyy h:mm a');
    }
  }
  return '';
}

/** MUI TextField multiline matching WarrQueue Repair card Correction field (same component style). */
function LastCommentTextArea({ name }: { name: string }): JSX.Element {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }): React.ReactElement => (
        <TextField
          {...field}
          multiline
          minRows={1}
          label="Last Comment"
          placeholder="Enter a message"
          fullWidth
          size="small"
          value={field.value ?? ''}
          sx={{
            '& .MuiInputBase-input': { fontSize: '13px', overflow: 'hidden', resize: 'none' },
            '& .MuiInputLabel-root': { fontSize: '13px' },
            '& .MuiFormHelperText-root': { fontSize: '11px' },
            '& .MuiInputBase-root': { paddingTop: '8px', alignItems: 'flex-start' },
          }}
        />
      )}
    />
  );
}

export interface TicketTableRowProps {
  row: TicketI;
  columnOrder: Column[];
  isItemSelected: boolean;
  handleClick: (
    event: MouseEvent<unknown>,
    id: string,
    roofTopId: string,
    roNumber: string,
    lineNumber: string | null
  ) => void;
  handleCheckClick: (ticketId: string, roofTopId: string) => void;
  isEditMode: boolean;
  selectedTickets: string[];
  formMethods: UseFormReturn<Record<string, ChangeTicketsListFormValues>>;
  ticketPrioritiesOptions: SelectOptionT[];
  ticketPrioritiesOptionsWithIcon: MuiFormSelectOptionT[];
  ticketSubcategoriesOptions: SelectOptionT[];
  claimStatusesOptions: SelectOptionT[];
  getAssigneeOptions: (roofTopId: string) => SelectOptionT[];
  getAdvisorOptions: (roofTopId: string) => SelectOptionT[];
  getTechOptions: (roofTopId: string) => SelectOptionT[];
  isLoadingTicketPrioritiesOptions: boolean;
  isLoadingTicketSubcategoriesOptions: boolean;
  isLoadingClaimStatusesOptions: boolean;
  isMobile: boolean;
  isScheduleSection?: boolean;
  /** When true, add data-label to cells for mobile card layout */
  dataLabelForCard?: boolean;
}

export const TicketTableRow: FC<TicketTableRowProps> = ({
  row,
  columnOrder,
  isItemSelected,
  handleClick,
  handleCheckClick,
  isEditMode,
  selectedTickets,
  formMethods: _formMethods,
  ticketPrioritiesOptions: _ticketPrioritiesOptions,
  ticketPrioritiesOptionsWithIcon,
  ticketSubcategoriesOptions,
  claimStatusesOptions,
  getAssigneeOptions,
  getAdvisorOptions,
  getTechOptions,
  isLoadingTicketPrioritiesOptions,
  isLoadingTicketSubcategoriesOptions,
  isLoadingClaimStatusesOptions,
  isMobile,
  isScheduleSection = false,
  dataLabelForCard = false,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const visibleColumns = columnOrder.filter(c => c.visible !== false);
  const isSelected = selectedTickets.includes(row.id);
  const isSameAuditEvent =
    !row.updatedDateTime &&
    !row.updatedByUser &&
    isUpdateCausedByLastMessage(row.detailUpdatedDateTime, row.lastMessage?.sentDate);

  const handleCheck = (e: MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
    handleCheckClick(row.id, row.roofTop.id);
  };

  // Match WarrQueue WorkItemRow: assigned/assignedHover for "needs attention" (unviewed), hoverNeutral for hover on others
  const resolveBackground = (theme: {
    palette: { workItemRow: { assigned: string; assignedHover: string; hoverNeutral: string } };
  }): string => {
    const isHighlighted = !row.isViewed; // unviewed = like "assigned to me" (needs attention)
    if (isHovered && isHighlighted) return theme.palette.workItemRow.assignedHover;
    if (!isHovered && isHighlighted) return theme.palette.workItemRow.assigned;
    if (isHovered && !isHighlighted) return theme.palette.workItemRow.hoverNeutral;
    return 'inherit';
  };

  return (
    <TableRow
      hover
      onMouseEnter={(): void => setIsHovered(true)}
      onMouseLeave={(): void => setIsHovered(false)}
      onDoubleClick={(event): void => {
        if (!isEditMode || !selectedTickets.includes(row.id)) {
          handleClick(event, row.id, row.roofTop.id, row.roNumber, row.lineNumber ?? null);
        }
      }}
      role="checkbox"
      tabIndex={-1}
      selected={isItemSelected}
      data-testid={`ticket-row-${row.id}`}
      sx={{
        cursor: 'pointer',
        backgroundColor: (theme): string => `${resolveBackground(theme)} !important`,
        ...(row.ticketPriority === 'Critical' && {
          borderLeft: theme => `3px solid ${theme.palette.error.main}`,
        }),
      }}
    >
      <TableCell
        padding="checkbox"
        {...(dataLabelForCard && { 'data-label': 'Select' })}
        onClick={(e): void => e.stopPropagation()}
        sx={{
          position: 'relative',
          backgroundColor: 'transparent',
          boxShadow: theme => `0 1px 0 0 ${theme.palette.divider}`,
          minWidth: CHECKBOX_COLUMN_WIDTH,
          width: CHECKBOX_COLUMN_WIDTH,
          overflow: 'visible',
        }}
      >
        <Box display="flex" justifyContent="flex-start" alignItems="center" height="100%">
          <Checkbox
            size="medium"
            color="primary"
            checked={isItemSelected}
            onClick={handleCheck}
            sx={{
              width: CHECKBOX_SIZE,
              height: CHECKBOX_SIZE,
              opacity: 1,
              visibility: 'visible',
              '& .MuiSvgIcon-root': { fontSize: CHECKBOX_ICON_FONT_SIZE },
            }}
          />
        </Box>
      </TableCell>
      {!dataLabelForCard && (
        <TableCell
          sx={{
            boxShadow: theme => `0 1px 0 0 ${theme.palette.divider}`,
            width: 28,
            minWidth: 28,
            padding: '0 4px',
            textAlign: 'center',
          }}
        >
          {!row.isViewed && (
            <FiberManualRecord
              sx={{
                color: 'primary.main',
                fontSize: 12,
                display: 'block',
              }}
            />
          )}
        </TableCell>
      )}
      {visibleColumns.map(col => {
        if (col.label === 'P') {
          return (
            <TableCell
              key={col.id}
              align="center"
              {...(dataLabelForCard && { 'data-label': col.label })}
              onClick={(e): void => e.stopPropagation()}
            >
              {isEditMode && isSelected ? (
                <Box display="flex" justifyContent="center" sx={{ maxWidth: 54, minWidth: 32 }}>
                  <MuiFormSelect
                    label="Priority"
                    name={`[${row.id}].ticketPriorityId` as never}
                    placeholder="Priority"
                    options={ticketPrioritiesOptionsWithIcon}
                    isLoading={isLoadingTicketPrioritiesOptions}
                    renderSelectedValue={(opt): React.ReactNode => (
                      <Box display="flex" alignItems="center" sx={opt.sxProps ?? undefined}>
                        {opt.icon}
                      </Box>
                    )}
                    sx={{
                      width: '100%',
                      maxWidth: 54,
                      minWidth: 32,
                      '& .MuiSelect-select': {
                        paddingRight: '28px !important',
                        paddingLeft: '8px !important',
                        paddingTop: '6px !important',
                        paddingBottom: '6px !important',
                        display: 'flex',
                        alignItems: 'center',
                        minWidth: 0,
                      },
                      '& .MuiOutlinedInput-root': { height: 'auto' },
                      '& .MuiInputLabel-shrink': {
                        transform: 'translate(14px, -6px) scale(0.75)',
                      },
                    }}
                  />
                </Box>
              ) : (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  title={row.ticketPriority ?? ''}
                >
                  {((): React.ReactNode => {
                    const opt = ticketPrioritiesOptionsWithIcon.find(
                      o => o.label === row.ticketPriority
                    );
                    if (!opt?.icon) return <Typography variant="body2">-</Typography>;
                    return (
                      <Box display="flex" alignItems="center" sx={opt.sxProps ?? undefined}>
                        {opt.icon}
                      </Box>
                    );
                  })()}
                </Box>
              )}
            </TableCell>
          );
        }
        if (col.label === 'RO') {
          return (
            <TableCell
              key={col.id}
              align="left"
              {...(dataLabelForCard && { 'data-label': col.label })}
            >
              <TruncatedWithTooltip value={row.roNumber} />
            </TableCell>
          );
        }
        if (col.label === 'Line') {
          return (
            <TableCell
              key={col.id}
              align="left"
              {...(dataLabelForCard && { 'data-label': col.label })}
            >
              <TruncatedWithTooltip value={row.lineNumber} />
            </TableCell>
          );
        }
        if (col.label === 'Age') {
          return (
            <TableCell
              key={col.id}
              align="center"
              {...(dataLabelForCard && { 'data-label': col.label })}
            >
              <Typography variant="body2" color={mapAgeToColor(row.age) as 'text.primary'}>
                {row.age}
              </Typography>
            </TableCell>
          );
        }
        if (col.label === 'Claim Age') {
          return (
            <TableCell
              key={col.id}
              align="center"
              {...(dataLabelForCard && { 'data-label': col.label })}
            >
              <Typography
                variant="body2"
                color={
                  row.claimAge ? (mapAgeToColor(row.claimAge) as 'text.primary') : 'text.primary'
                }
              >
                {row.claimAge ?? '-'}
              </Typography>
            </TableCell>
          );
        }
        if (col.label === 'Closed RO Age') {
          return (
            <TableCell
              key={col.id}
              align="center"
              {...(dataLabelForCard && { 'data-label': col.label })}
            >
              <Typography
                variant="body2"
                color={
                  row.closedROAge != null
                    ? (mapAgeToColor(row.closedROAge) as 'text.primary')
                    : 'text.primary'
                }
              >
                {row.closedROAge ?? '-'}
              </Typography>
            </TableCell>
          );
        }
        if (col.label === 'Closed RO Date') {
          const closedRoDateText = formatClosedRoDateForList(row.roClosedDate);
          return (
            <TableCell
              key={col.id}
              align="left"
              {...(dataLabelForCard && { 'data-label': col.label })}
            >
              {closedRoDateText === '' ? null : <TruncatedWithTooltip value={closedRoDateText} />}
            </TableCell>
          );
        }
        if (col.label === 'Status') {
          return (
            <TableCell
              key={col.id}
              align="left"
              {...(dataLabelForCard && { 'data-label': col.label })}
            >
              <Typography variant="body2" color="text.primary">
                {row.statusId}
              </Typography>
            </TableCell>
          );
        }
        if (col.label === 'Assignee') {
          return (
            <TableCell
              key={col.id}
              align="left"
              {...(dataLabelForCard && { 'data-label': col.label })}
              onClick={(e): void => e.stopPropagation()}
            >
              {isEditMode && isSelected ? (
                <Box>
                  <MuiFormSelect
                    label="Assignee"
                    name={`[${row.id}].assigneeName` as never}
                    placeholder=""
                    options={getAssigneeOptions(row.roofTop.id)}
                  />
                </Box>
              ) : (
                <TruncatedWithTooltip
                  value={row.assigneeName}
                  color={
                    row.assigneeName === TicketAssigneeE.WarrCloud ? 'secondary' : 'text.primary'
                  }
                />
              )}
            </TableCell>
          );
        }
        if (col.label === 'Category') {
          return (
            <TableCell
              key={col.id}
              align="left"
              {...(dataLabelForCard && { 'data-label': col.label })}
            >
              <TruncatedWithTooltip value={row.categoryName} />
            </TableCell>
          );
        }
        if (col.label === 'Subcategory') {
          return (
            <TableCell
              key={col.id}
              align="left"
              {...(dataLabelForCard && { 'data-label': col.label })}
              onClick={(e): void => e.stopPropagation()}
            >
              {isEditMode && isSelected && row.categoryName?.toLowerCase() === 'schedule' ? (
                <Box>
                  <MuiFormSelect
                    label="Subcategory"
                    name={`[${row.id}].subCategoryId` as never}
                    placeholder="Subcategories"
                    options={ticketSubcategoriesOptions}
                    isLoading={isLoadingTicketSubcategoriesOptions}
                    searchable
                  />
                </Box>
              ) : (
                <TruncatedWithTooltip
                  value={row.subCategory ?? row.requestSubCategoryName ?? undefined}
                />
              )}
            </TableCell>
          );
        }
        if (col.label === 'Account') {
          return (
            <TableCell
              key={col.id}
              align="left"
              {...(dataLabelForCard && { 'data-label': col.label })}
              onClick={(e): void => e.stopPropagation()}
            >
              {isEditMode && isSelected && row.categoryName?.toLowerCase() === 'schedule' ? (
                <Box>
                  <MuiFormInput
                    label="Account number"
                    name={`[${row.id}].dmsScheduleAccountNumber` as never}
                    placeholder="Account number"
                  />
                </Box>
              ) : (
                <Typography variant="body2" color="text.primary">
                  {row.dmsScheduleAccountNumber ?? ''}
                </Typography>
              )}
            </TableCell>
          );
        }
        if (col.label === 'Work Type') {
          return (
            <TableCell
              key={col.id}
              align="left"
              {...(dataLabelForCard && { 'data-label': col.label })}
              onClick={(e): void => e.stopPropagation()}
            >
              {isEditMode && isSelected && row.categoryName?.toLowerCase() === 'schedule' ? (
                <Box>
                  <MuiFormSelect
                    label="Work Type"
                    name={`[${row.id}].workTypeId` as never}
                    placeholder="Work Type"
                    options={Object.values(ScheduleWorkTypeE).map(value => ({
                      label: value,
                      value,
                    }))}
                  />
                </Box>
              ) : (
                <TruncatedWithTooltip value={row.workType ?? undefined} />
              )}
            </TableCell>
          );
        }
        if (col.label === 'Last Comment') {
          return (
            <TableCell
              key={col.id}
              align="left"
              {...(dataLabelForCard && { 'data-label': col.label })}
              onClick={(e): void => e.stopPropagation()}
              sx={{
                verticalAlign: 'middle',
                overflow: 'visible',
                whiteSpace: 'normal',
                textOverflow: 'clip',
              }}
            >
              {isEditMode && isSelected ? (
                <LastCommentTextArea name={`[${row.id}].text` as never} />
              ) : (
                <>
                  {row.lastMessage?.sourceId === MessageSourcesE.ScheduleImport && (
                    <Typography variant="body2" color="error" component="span">
                      [Import]&nbsp;
                    </Typography>
                  )}
                  <Typography
                    variant="body2"
                    color="text.primary"
                    sx={{
                      whiteSpace: 'pre-wrap',
                      overflowWrap: 'break-word',
                      display: 'block',
                    }}
                  >
                    {preventEarlyWrap(getLastMessageDisplayText(row.lastMessage))}
                  </Typography>
                </>
              )}
            </TableCell>
          );
        }
        if (col.label === 'Claim Status') {
          return (
            <TableCell
              key={col.id}
              align="left"
              {...(dataLabelForCard && { 'data-label': col.label })}
              onClick={(e): void => e.stopPropagation()}
            >
              {isEditMode && isSelected ? (
                <Box>
                  <MuiFormSelect
                    label="Claim Status"
                    name={`[${row.id}].claimStatusId` as never}
                    placeholder="Claim statuses"
                    options={claimStatusesOptions}
                    isLoading={isLoadingClaimStatusesOptions}
                  />
                </Box>
              ) : (
                <Typography variant="body2" color="text.primary">
                  {row.claimStatus}
                </Typography>
              )}
            </TableCell>
          );
        }
        if (col.label === 'Amount') {
          return (
            <TableCell
              key={col.id}
              align="left"
              {...(dataLabelForCard && { 'data-label': col.label })}
              onClick={(e): void => e.stopPropagation()}
            >
              {isEditMode && isSelected && !isScheduleSection ? (
                <Box>
                  <MuiFormInput
                    label="Amount"
                    name={`[${row.id}].amount` as never}
                    placeholder="0.00"
                    type="number"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <AttachMoneyIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              ) : (
                <Typography variant="body2" color="text.primary">
                  {formatAmount(row.amount)}
                </Typography>
              )}
            </TableCell>
          );
        }
        if (col.label === 'Advisor') {
          const advisorOptions = getAdvisorOptions(row.roofTop.id);
          return (
            <TableCell
              key={col.id}
              align="left"
              {...(dataLabelForCard && { 'data-label': col.label })}
              onClick={(e): void => e.stopPropagation()}
            >
              {isEditMode && isSelected ? (
                <Box>
                  {advisorOptions.length > 0 ? (
                    <MuiFormAutocomplete
                      label="Advisor"
                      name={`[${row.id}].advisorNumber` as never}
                      placeholder="Select or enter advisor"
                      options={advisorOptions}
                      allowCustomInput
                    />
                  ) : (
                    <MuiFormInput
                      label="Advisor"
                      name={`[${row.id}].advisorNumber` as never}
                      placeholder="Enter advisor"
                    />
                  )}
                </Box>
              ) : (
                <TruncatedWithTooltip value={row.advisorNumber} />
              )}
            </TableCell>
          );
        }
        if (col.label === 'VIN') {
          return (
            <TableCell
              key={col.id}
              align="left"
              {...(dataLabelForCard && { 'data-label': col.label })}
            >
              <TruncatedWithTooltip value={row.vin ?? undefined} />
            </TableCell>
          );
        }
        if (col.label === 'Tech') {
          const techOptions = getTechOptions(row.roofTop.id);
          return (
            <TableCell
              key={col.id}
              align="left"
              {...(dataLabelForCard && { 'data-label': col.label })}
              onClick={(e): void => e.stopPropagation()}
            >
              {isEditMode && isSelected ? (
                <Box>
                  {techOptions.length > 0 ? (
                    <MuiFormAutocomplete
                      label="Tech"
                      name={`[${row.id}].techNumber` as never}
                      placeholder="Select or enter tech"
                      options={techOptions}
                      allowCustomInput
                    />
                  ) : (
                    <MuiFormInput
                      label="Tech"
                      name={`[${row.id}].techNumber` as never}
                      placeholder="Enter tech"
                    />
                  )}
                </Box>
              ) : (
                <TruncatedWithTooltip value={row.techNumber} />
              )}
            </TableCell>
          );
        }
        if (col.label === 'Attachment') {
          return (
            <TableCell
              key={col.id}
              align="left"
              {...(dataLabelForCard && { 'data-label': col.label })}
            >
              {row.hasAttachment && <ApproveIcon size={isMobile ? 'xl' : '3xl'} />}
            </TableCell>
          );
        }
        if (col.label === 'Chirp') {
          if (row.chirpTicketInfo) {
            const isLastFromChirp = row.chirpTicketInfo.userType === TicketUserTypesE.Chirp;
            const isChirpTicketClosed = row.chirpTicketInfo.status === StatusIdE.Closed;
            const lastChirpMessage = row.chirpTicketInfo.lastMessage?.trim();
            const statusLine = isChirpTicketClosed ? 'Closed Chirp ticket' : null;
            const responderLine = `Last Response: ${isLastFromChirp ? 'Chirp' : 'WarrCloud'}`;
            return (
              <TableCell
                key={col.id}
                align="left"
                {...(dataLabelForCard && { 'data-label': col.label })}
              >
                <Tooltip
                  title={
                    <>
                      {statusLine ?? responderLine}
                      {!isChirpTicketClosed ? null : (
                        <>
                          <br />
                          {responderLine}
                        </>
                      )}
                      {lastChirpMessage ? (
                        <>
                          <br />
                          <br />
                          {lastChirpMessage}
                        </>
                      ) : null}
                    </>
                  }
                  componentsProps={{
                    tooltip: {
                      sx: { maxWidth: 360, whiteSpace: 'pre-wrap', wordBreak: 'break-word' },
                    },
                  }}
                >
                  <ChatIcon
                    sx={getChirpChatIconSx(isMobile, isLastFromChirp, isChirpTicketClosed)}
                  />
                </Tooltip>
              </TableCell>
            );
          }
          return (
            <TableCell
              key={col.id}
              align="left"
              {...(dataLabelForCard && { 'data-label': col.label })}
            >
              <Tooltip title="No Open Chirp Ticket">
                <Box
                  width={24}
                  height={24}
                  display="inline-flex"
                  alignItems="center"
                  justifyContent="center"
                />
              </Tooltip>
            </TableCell>
          );
        }
        if (col.label === 'Approved') {
          return (
            <TableCell
              key={col.id}
              align="left"
              {...(dataLabelForCard && { 'data-label': col.label })}
            >
              {row.isApprovedByDealer && <ApproveIcon size={isMobile ? 'xl' : '3xl'} />}
            </TableCell>
          );
        }
        if (col.label === 'Audit Trail') {
          let auditInitials = '-';
          if (isSameAuditEvent && row.lastMessage?.sender?.displayName) {
            auditInitials = getInitials(row.lastMessage.sender.displayName);
          } else if (!isSameAuditEvent && row.updatedByUser?.displayName) {
            auditInitials = getInitials(row.updatedByUser.displayName);
          }
          const auditDate = formatAuditTrailDisplayDate(row);
          return (
            <TableCell
              key={col.id}
              align="left"
              {...(dataLabelForCard && { 'data-label': col.label })}
            >
              <Typography variant="body2" fontWeight="bold" component="span">
                {auditInitials}
              </Typography>
              <Typography variant="body2" component="span" display="block">
                {auditDate}
              </Typography>
            </TableCell>
          );
        }
        return <TableCell key={col.id} />;
      })}
    </TableRow>
  );
};
